import type { ColumnDef } from '@tanstack/react-table';
import { AccountHolderCell, DateCell } from '@/components/data-table/cells';
import { CoinCell } from '@/components/ui/coin-cell';
import { CopyButton } from '@/components/ui/copy-button';
import { DIRECTION_LABELS, type Transaction } from '@/services/api/funds-transactions';
import { shortUuid } from '@/utils/string';

const COIN_ICON_SIZE = 22;

const EMPTY_PLACEHOLDER = <span className="text-muted-foreground">—</span>;

// Legacy rendered these cells as v-html strings with inline onClick handlers
// (tooltipify) — reimplemented as real components (XSS fix).

/** Truncated UUID + full-value tooltip + copy affordance */
const CopyableUuidCell = ({ value }: { value: string | undefined }) => {
  if (!value) return EMPTY_PLACEHOLDER;
  return (
    <span className="inline-flex items-center gap-1 font-mono text-xs" title={value}>
      {shortUuid(value)}
      <CopyButton label={`Copy ${value}`} value={value} />
    </span>
  );
};
CopyableUuidCell.displayName = 'CopyableUuidCell';

/** Truncated UUID + tooltip only (destination/source accounts per legacy config) */
const TooltipUuidCell = ({ value }: { value: string | undefined }) => {
  if (!value) return EMPTY_PLACEHOLDER;
  return (
    <span className="font-mono text-xs" title={value}>
      {shortUuid(value)}
    </span>
  );
};
TooltipUuidCell.displayName = 'TooltipUuidCell';

const RawCell = ({ value }: { value: unknown }) => {
  if (value === null || value === undefined || value === '') return EMPTY_PLACEHOLDER;
  return <span>{String(value)}</span>;
};
RawCell.displayName = 'RawCell';

/**
 * Visible columns from the legacy column config, in order. Hidden legacy columns
 * (user_confirmed_at, completed_at, source_account, net_amount, fee_amount, iban,
 * bic) have no toggle and only ever surfaced through the detail dialog, which
 * iterates every record key — so they need no column defs here.
 *
 * Sorting is disabled everywhere: the API paginates by cursor, so sorting would
 * only reorder the current 40 rows and mislead.
 */
export const TRANSACTION_TABLE_COLUMNS: ColumnDef<Transaction, unknown>[] = [
  {
    accessorKey: 'user_requested_at',
    header: 'Requested Date',
    enableSorting: false,
    cell: ({ row }) => <DateCell value={row.original.user_requested_at} />,
  },
  {
    accessorKey: 'account_holder_id',
    header: 'Account Holder PID',
    enableSorting: false,
    cell: ({ row }) => <AccountHolderCell value={row.original.account_holder_id} />,
  },
  {
    accessorKey: 'account_id',
    header: 'Account ID',
    enableSorting: false,
    cell: ({ row }) => <CopyableUuidCell value={row.original.account_id} />,
  },
  {
    accessorKey: 'direction',
    header: 'Direction',
    enableSorting: false,
    cell: ({ row }) => <RawCell value={DIRECTION_LABELS[row.original.direction] ?? row.original.direction} />,
  },
  {
    accessorKey: 'amount',
    header: 'Amount',
    enableSorting: false,
    cell: ({ row }) => <span className="whitespace-nowrap tabular-nums">{row.original.amount}</span>,
  },
  {
    accessorKey: 'currency',
    header: 'Currency',
    enableSorting: false,
    cell: ({ row }) => <CoinCell asset={row.original.currency} size={COIN_ICON_SIZE} />,
  },
  {
    accessorKey: 'transfer_status',
    header: 'Status',
    enableSorting: false,
    cell: ({ row }) => <RawCell value={row.original.transfer_status} />,
  },
  {
    accessorKey: 'transfer_provider',
    header: 'Provider',
    enableSorting: false,
    cell: ({ row }) => <RawCell value={row.original.transfer_provider} />,
  },
  {
    accessorKey: 'transfer_method_type',
    header: 'Method',
    enableSorting: false,
    cell: ({ row }) => <RawCell value={row.original.transfer_method_type} />,
  },
  {
    accessorKey: 'transaction_id',
    header: 'Transaction ID',
    enableSorting: false,
    cell: ({ row }) => <CopyableUuidCell value={row.original.transaction_id} />,
  },
  {
    accessorKey: 'destination_account',
    header: 'Destination Account',
    enableSorting: false,
    cell: ({ row }) => <TooltipUuidCell value={row.original.destination_account} />,
  },
  {
    accessorKey: 'payout_account_label',
    header: 'Account Label',
    enableSorting: false,
    cell: ({ row }) => <RawCell value={row.original.payout_account_label} />,
  },
  {
    accessorKey: 'broker_batch_id',
    header: 'Broker Batch ID',
    enableSorting: false,
    cell: ({ row }) => <RawCell value={row.original.broker_batch_id} />,
  },
];
